// react
import { useMemo } from 'react';

// feature-utils
import { supportedUnitTypeIds } from '@/features/energyxplorer/utils/supportedUnitTypes';

/**
 * Custom hook to decorate units with usage information and validation rules.
 *
 * This hook evaluates which units are already in use within the energy tree,
 * whether they are supported unit types, and if they meet calibration requirements.
 * It works with both server state (enhancedEnergyGroup) and draft state (draftTree),
 * ensuring real-time validation as users make changes.
 *
 * @param {Array} units - Array of unit objects from the API
 * @param {Array} treeNodes - Array of tree nodes (can be server state or draft state)
 * @returns {Array} Decorated units with additional properties:
 *   - usedInNode: Name of the node where this unit is used (or null)
 *   - notSupportedReason: Reason why unit cannot be used (or null)
 *   - isDisabled: Boolean indicating if unit should be disabled in selection
 */
export const useDecoratedUnits = (units, treeNodes) => {
  return useMemo(() => {
    const map = new Map();

    // Build map of unitId -> nodeName for all units currently in use
    treeNodes?.forEach((node) => {
      node?.dataStreams?.forEach((stream) => {
        if (stream.unitId != null) {
          map.set(stream.unitId, node.name);
        }
      });
    });

    return units?.map((unit) => {
      const usedInNode = map.get(unit.UnitId) || null;
      const isSupported = supportedUnitTypeIds.includes(
        unit.UnitType?.UnitTypeId,
      );
      const isSensorUncalibrated =
        unit.UnitType?.UnitTypeId === 28 &&
        unit?.UnitSensors?.[0]?.Sensor?.CalibrationType === 'Level1';

      let notSupportedReason = null;
      if (!isSupported) {
        notSupportedReason = 'Unsupported unit type';
      } else if (isSensorUncalibrated) {
        notSupportedReason = 'Sensor is uncalibrated (Level 1)';
      }

      return {
        ...unit,
        usedInNode,
        notSupportedReason,
        isDisabled: Boolean(
          usedInNode || notSupportedReason || isSensorUncalibrated,
        ),
      };
    });
  }, [units, treeNodes]);
};
